import { useState, useRef } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { getOAuthHeaders } from "../../../../constants/oAuthValidation";
import { timeStamp } from "../../../../utils/clock";
import db from "../../../../database";
import { verifyReturn } from "../../../../services";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import useRestaurantStore from "../../../../stores/useRestaurantStore";
import { getIsInterState } from "../../../../utils/taxUtils";
import { groupItemsByTaxRate } from "../utils/taxCalculations";

const useSalesReturn = ({ cart, setCart, tillData, precision, posConfig, isExchange, loading, setLoading, setKeyValue, giftCardItems, setSideMenuDrawervisible }) => {
  const { t } = useTranslation();

  // Return order state
  const [selectedReturnOrder, setSelectedReturnOrder] = useState({});
  const [dataSource, setDataSource] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [retrunSearchType, setReturnSearchType] = useState("orderNo");
  const [showReturnPurchase, setShowReturnPurchase] = useState(false);
  const [showReturnOptions, setShowReturnOptions] = useState(false);
  const [isBlindReceiptPending, setIsBlindReceiptPending] = useState(false);
  const [isPurchaseReturn, setIsPurchaseReturn] = useState(false);
  const selectedReturnType = useTransactionStore((s) => (s.selectedReturnType && s.selectedReturnType !== "null" ? s.selectedReturnType : null));
  const setSelectedReturnType = useTransactionStore((s) => s.setSelectedReturnType);
  const [displayReturnOrderSearch, setDisplayReturnOrderSearch] = useState(false);

  const returnProcessRef = useRef();
  const returnCartInFlightRef = useRef(false);

  // Internal index counter for selectPurchaseBillForReturn (mirrors the let index = 0 pattern)
  let selectIndex = 0;

  const handleSalesReturnFromSideMenu = () => {
    setSideMenuDrawervisible(false);
    setDisplayReturnOrderSearch(true);
  };

  const selectPurchaseBillForReturn = async (data) => {
    if (selectIndex === 0) {
      let returnBillItem = data[0];

      // DI-24: Return idempotency — check local DB for existing returns against this receipt
      // try {
      //   const originalOrderId = returnBillItem.sOrderID;
      //   if (originalOrderId) {
      //     const allLocalOrders = await db.orders.toArray();
      //     const alreadyReturned = allLocalOrders.filter((o) => (o.isReturn === true || o.isReturn === "Y") && o.items?.some((item) => item.sOrderReturnId === originalOrderId));
      //     if (alreadyReturned.length > 0) {
      //       message.error("This receipt has already been returned. Duplicate returns are not allowed.");
      //       console.error("[DI-24] Duplicate return blocked", {
      //         originalOrderId,
      //         existingReturnOrderIds: alreadyReturned.map((o) => o.sOrderID),
      //       });
      //       return;
      //     }
      //   }
      // } catch (err) {
      //   // Non-blocking — log but allow flow to continue in case of DB read failure
      //   console.error("[DI-24] Error checking return idempotency:", err);
      // }

      setDisplayReturnOrderSearch(false);
      setReturnSearchType("orderNo");

      const discountValue = returnBillItem.discAmount;
      let grossstdTotal = 0;
      for (let i = 0; i < returnBillItem.line.length; i += 1) {
        grossstdTotal += parseFloat(returnBillItem.line[i].grossstd);
      }
      const zeroDiscountCount = returnBillItem.line.filter((item) => item.discount === 0).length;

      if (zeroDiscountCount === returnBillItem.line.length && discountValue > 0) {
        returnBillItem.line.forEach((addedToCart, index) => {
          const discountAmt = parseFloat((addedToCart.grossstd / grossstdTotal) * discountValue) / addedToCart.qty;
          const sp = parseFloat(addedToCart.grossstd) - discountAmt;
          addedToCart.grossstd = sp;
          addedToCart.linestd = sp * addedToCart?.weight;
          returnBillItem.line[index] = addedToCart;
        });
      }
      const updatedDataSource = [...returnBillItem.line];
      let itemsToReturn = 0;
      let itemsQtyToReturn = 0;
      let returnAmountTotal = 0;

      updatedDataSource.forEach((_, i) => {
        updatedDataSource[i].key = `${i}`;
        updatedDataSource[i].remainingQty = updatedDataSource[i].returnQty > 0 ? updatedDataSource[i].qty - updatedDataSource[i].returnQty : updatedDataSource[i].qty;
        updatedDataSource[i].qtyToReturn = updatedDataSource[i].returnQty > 0 ? updatedDataSource[i].qty - updatedDataSource[i].returnQty : updatedDataSource[i].qty;
        itemsToReturn += 1;
        itemsQtyToReturn += updatedDataSource[i].qtyToReturn > 0 ? updatedDataSource[i].qtyToReturn : updatedDataSource[i].qty;
        if (updatedDataSource[i].linegross && updatedDataSource[i].qty) {
          const unitGross = Math.abs(updatedDataSource[i].linegross) / Math.abs(updatedDataSource[i].qty);
          returnAmountTotal += updatedDataSource[i].qtyToReturn * unitGross;
        } else {
          // Fallback to old calculation (only runs if return data is missing fields)
          returnAmountTotal += updatedDataSource[i].qtyToReturn * parseFloat(updatedDataSource[i].grossstd || 0);
        }
      });
      updatedDataSource.itemsToReturn = 0;
      updatedDataSource.itemsQtyToReturn = 0;
      updatedDataSource.returnAmountTotal = 0;

      returnBillItem.itemsToReturn = itemsToReturn;
      returnBillItem.itemsQtyToReturn = itemsQtyToReturn;
      returnBillItem.returnAmountTotal = returnAmountTotal;
      setDataSource([...updatedDataSource]);
      setSelectedReturnOrder({ ...returnBillItem });
      selectIndex++;
    }
  };

  const searchReturnHistory = async (documentno, record) => {
    if (loading) return;
    const searchQuery = `${retrunSearchType} : "${documentno}"`;
    const authHeaders = getOAuthHeaders();
    if (authHeaders && authHeaders.access_token) {
      try {
        setLoading(true);
        const data = {
          query: `query {
                    verifyReturn(${searchQuery}) {
                        sOrderID
                        created
                        createdby
                        updated
                        updatedby
                        accumulation
                        redemption
                        documentno
                        dateordered
                        totalQty
                        orderTime
                        cwrProductQty
                        taxamt
                        grosstotal
                        discAmount
                        csBUnit {
                            csBunitId
                            name
                        }
                        cwrB2cCustomer {
                            cwrCustomerId
                            code
                            name
                            mobileNo
                            creditLimit
                            iscredit
                            creditBalance
                            creditApproachPercentage
                            pincode
                            email
                            retlLoyaltyBalance
                            loyaltyLevel{
                              cwrLoyaltyLevelId
                              name
                              accumulationRate
                              redemptionRate
                          }
                          b2cCustomerSegment{
                            name
                            cwrB2CCustomerSegmentId
                             }
                            sCustomer {
                                sCustomerID
                                customerCategory {
                                    sCustomerCateforyId
                                    value
                                    name
                                    description
                                }
                            }
                            cwrWallet {
                              balance_Amt
                              wallet_Id
                            }
                        }
                        saleType {
                            cwrSaletypeId
                            name
                            value
                        }
                        cwrTill {
                            cwrTillID
                            till
                        }
                        finReceiptPlan {
                            finReceiptPlanDetails {
                                amount
                                cwrPaymentmethod {
                                    cWRPaymentMethodID
                                    finFinancialAccountId
                                    finPaymentmethodId
                                    integratedPayment
                                    isloyalty
                                    paymentProvider
                                }
                            }
                        }
                        line {
                            sOrderlineID
                            sOrderId
                            line
                            description
                            qty
                            netlist
                            netunit
                            linetax
                            unittax
                            linenet
                            linegross
                            grosslist
                            grossstd
                            returnline
                            returnQty
                            discount
                            product {
                                mProductId
                                name
                                value
                                upc
                                hsncode
                                imageurl
                                isManualQty
                                shortDescription
                                returnable
                                returnDays
                            }
                            uom {
                                csUomId
                                name
                            }
                            tax {
                                csTaxID
                                name
                                rate
                            }
                            pricingRule {
                              mPricingrulesId
                              name
                              discount
                            }
                        }
                        metaData {
                            key
                            value
                        }
                    }
                }`,
        };
        const searchData = await verifyReturn(data);
        let matchedRecord = searchData.data.data.verifyReturn.filter((item) => item.sOrderID === record.sOrderID);
        await selectPurchaseBillForReturn(matchedRecord);
        setShowReturnPurchase(true);
        return matchedRecord[0];
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const getProductData = (data, itemData) => {
    return new Promise(function (resolve) {
      db.products
        .where("mProductId")
        .equalsIgnoreCase(data)
        .toArray()
        .then((allProducts) => allProducts.filter((p) => p.isactive == true && p.productSegment !== "BG"))
        .then((product) => {
          if (product.length > 0) {
            const obj = { ...product[0] };
            if (obj.overRideTax === "Y" && obj.sunitprice <= obj.overRideCondition) {
              // prettier-ignore
              const originalPrice = obj.sunitprice - (obj.sunitprice - (obj.sunitprice * (100 / (100 + obj.taxRate))));
              const taxedPrice = originalPrice + (originalPrice * obj.contraRate) / 100;
              obj.sunitprice = taxedPrice;
              obj.cTaxId = obj.contraTaxId;
              obj.taxRate = obj.contraRate;
            }
            const productDefined = {
              batchno: null,
              description: obj.description,
              discount: itemData.discount * -1,
              productSegment: obj?.productSegment || "",
              imageurl: obj.imageurl,
              isDecimal: obj.isDecimal,
              isManualQty: obj.isManualQty,
              restrictPosQtyEdit: obj.restrictPosQtyEdit || "N",
              showAlternateUomInPos: obj?.showAlternateUomInPos || "N",
              interTaxId: obj?.interTaxId || "",
              interTaxRate: obj?.interTaxRate || 0,
              interTaxName: obj?.interTaxName || "",
              alternateUom: obj?.alternateUom || null,
              isPromoApplicable: false,
              isReturn: true,
              mBatchId: null,
              mPricingruleId: null,
              name: obj.name,
              name2: obj.name2,
              nettotal: itemData.linegross * -1,
              primaryOrderLine: null,
              productId: obj.mProductId,
              realPrice: obj.sunitprice * -1,
              listPrice: obj.slistprice * -1,
              sunitprice: obj.sunitprice * -1,
              returnQty: null,
              salePrice: itemData.grossstd * -1,
              unitTax: itemData.unittax * -1,
              mrpPrice: obj.sunitprice * -1,
              stock: obj.onhandQty,
              taxCategory: obj.taxCategory,
              taxName: obj?.taxName,
              taxFlag: obj?.taxFlag || "N",
              tax: obj.cTaxId,
              taxAmount: itemData.linetax * -1,
              taxRate: obj.taxRate,
              uom: obj.csUomId,
              uom_name: obj.uomName,
              isDecimalQty: obj.uomData?.length > 0 ? (obj.uomData[0].decimal === "Y" ? true : false) : false,
              isQtyDesimal: obj.uomData?.[0]?.stdprecision ?? 2,
              upc: obj.upc,
              value: obj.value,
              weight: 0,
              shortDescription: obj.shortDescription,
              hsncode: obj.hsncode,
              csBunitId: obj.csBunitId,
              mProductCategoryId: obj.mProductCategoryId,
              productManufacturerId: obj.productManufacturerId,
              productBrandId: obj.productBrandId,
              productBrandName: obj?.productBrandName,
              batchedProduct: obj.batchedProduct,
              batchedForSale: obj.batchedForSale,
              batchedForStock: obj.batchedForStock,
              multiPrice: obj.multiPrice,
              shelfLife: obj.shelfLife,
              discountName: itemData.pricingRule.name,
              mPricingruleId: itemData.pricingRule.mPricingrulesId,
            };
            resolve(productDefined);
          } else {
            {
              message.warning(`${t("product_search_error")}`);
            }
            resolve(null);
          }
        });
    });
  };

  const handleSaveReturnCartRecord = (data) => {
    let itemsToReturn = 0;
    let itemsQtyToReturn = 0;
    let returnAmountTotal = 0;
    data.forEach((item, i) => {
      if (item.qtyToReturn > 0) {
        itemsToReturn += 1;
        itemsQtyToReturn += item.qtyToReturn;
        returnAmountTotal += item.qtyToReturn * item.grossstd;
      }
    });

    selectedReturnOrder.line = data;
    selectedReturnOrder.itemsToReturn = itemsToReturn;
    selectedReturnOrder.itemsQtyToReturn = itemsQtyToReturn;
    selectedReturnOrder.returnAmountTotal = returnAmountTotal;
    setSelectedReturnOrder({ ...selectedReturnOrder });
  };

  const posLogActivity = (record, activity) => {
    const timeMark = timeStamp();
    const currentDate = new Date().toLocaleDateString("zh-Hans-CN");
    let products = "";

    const trxId = uuidv4().replace(/-/g, "").toUpperCase();

    if (Array.isArray(record)) {
      record.forEach((item) => {
        products += `SKU: ${item.value}, Qty: ${item.weight}, Unit Price: ${item.salePrice}, `;
      });
    } else {
      products = `SKU: ${record.value}, Qty: ${record.weight}, Unit Price: ${record.salePrice}, `;
    }

    db.logInformation.add({
      type: activity,
      action: "LOG",
      description: activity === "SLR" ? products : "",
      date: currentDate,
      time: timeMark,
      orderNo: `${cart.documentno}`,
      remarks: "",
      transactionId: `${trxId}`,
      status: "SCS",
      duration: null,
    });
  };

  const addSelectedReturnProductsToCart = async (keysOverride) => {
    if (returnCartInFlightRef.current) return;
    returnCartInFlightRef.current = true;
    try {
      const activeKeys = keysOverride || checkedKeys;
      if (!selectedReturnOrder?.line?.length) return;

      // DI-25: Pre-validate return amount before mutating cart
      {
        const originalTotal = Math.abs(Number(selectedReturnOrder.grosstotal) || 0);
        if (originalTotal > 0) {
          const estimatedReturnTotal = selectedReturnOrder.line
            .filter((item) => activeKeys.includes(item.key) && item.qtyToReturn > 0)
            .reduce((sum, item) => {
              if (item.linegross) {
                const unitGross = Math.abs(item.linegross) / Math.abs(item.qty);
                return sum + unitGross * item.qtyToReturn;
              }
              return sum + Math.abs(Number(item.netamt) || Number(item.linetotal) || 0) * (item.qtyToReturn / Math.abs(item.qty || 1));
            }, 0);
          if (estimatedReturnTotal > originalTotal + 0.01) {
            console.error("[DI-25] Return amount exceeds original order total", {
              estimatedReturnTotal,
              originalTotal,
              difference: estimatedReturnTotal - originalTotal,
            });
            message.error("Return amount cannot exceed the original order amount.");
            setLoading(false);
            return;
          }
        }
      }

      let cartItems = [...cart.items];
      for (let index = 0; index < selectedReturnOrder.line.length; index++) {
        const item = { ...selectedReturnOrder.line[index] };
        let giftCardItem = giftCardItems.filter((giftCard) => giftCard.productId === item.product.mProductId);
        const productItem = activeKeys.includes(item.key) && giftCardItem.length === 0 ? await getProductData(item.product.mProductId, item) : null;
        if (productItem && item.qtyToReturn > 0) {
          productItem.sOrderReturnId = item.sOrderId;
          productItem.sOrderlineReturnId = item.sOrderlineID;
          productItem.wastage = (useRestaurantStore.getState().wastageflag || localStorage.getItem("wastageFlag")) === "Y" ? "Y" : "N";
          if (item.returnQty < item.qty) {
            let discountAmount = productItem.discount / item.qty;
            productItem.weight = -item.qtyToReturn;
            const mrp = parseFloat(Math.abs(productItem.salePrice)) * parseFloat(item.qtyToReturn);
            const tax = productItem.taxAmount / item.qty;
            productItem.discount = discountAmount * item.qtyToReturn;
            productItem.taxAmount = tax * item.qtyToReturn;
            productItem.realPrice = item.grossstd > 0 ? item.grossstd : (item.netunit + item.linetax + item.discount) / item.qty;
            if (item.linegross) {
              const unitGross = Math.abs(item.linegross) / Math.abs(item.qty);
              productItem.nettotal = -(unitGross * item.qtyToReturn);
            } else {
              // fallback to old logic (normal sales)
              productItem.nettotal = mrp > 0 ? -Math.abs(mrp - parseFloat(productItem.discount ? Math.abs(productItem.discount) : 0)) : 0;
            }
            let netStd = parseFloat(Math.abs(productItem.sunitprice) - Math.abs(productItem.sunitprice) / (1 + Math.abs(productItem.taxRate) / 100));
            const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};
            let taxIncludeFlag = saleTypeData?.customer?.istaxincluded === "Y" ? "Y" : tillData.tillAccess.csBunit.isTaxIncluded ? tillData.tillAccess.csBunit.isTaxIncluded : "Y";
            productItem.netStd =
              taxIncludeFlag === "Y"
                ? parseFloat(((Math.abs(productItem.sunitprice) - netStd) * -1).toFixed(tillData.tillAccess.csBunit.currencies[0].prcPrecision))
                : productItem.sunitprice;
            cartItems.push(productItem);
          }
          if (
            db.logConfiguration.toArray().then((fetched) => {
              fetched.map((item) => {
                item.salesReturn.filter((item) => item.log === "Y").length > 0;
              });
            })
          ) {
            posLogActivity(productItem, "SLR");
          }
        }
      }

      const returnItemsAdded = cartItems.length - cart.items.length;
      if (returnItemsAdded === 0) {
        message.warning("No matching products found in the system for the selected return items.");
        setLoading(false);
        return;
      }

      // DI-25 post-mutation safety check (primary validation is pre-loop)
      {
        const returnTotal = cartItems.filter((item) => item.isReturn === true).reduce((sum, item) => sum + Math.abs(Number(item.nettotal) || 0), 0);
        const originalTotal = Math.abs(Number(selectedReturnOrder.grosstotal) || 0);
        if (originalTotal > 0 && returnTotal > originalTotal + 0.01) {
          console.error("[DI-25] Post-mutation return amount exceeds original", { returnTotal, originalTotal });
        }
      }

      const itemsSummery = {
        decimalItems: { totalQty: 0, totalAmount: 0 },
        nonDecimalItems: { totalQty: 0, totalAmount: 0 },
        itemsQty: 0,
        items: 0,
      };

      cartItems.map((item) => {
        if (Array.isArray(item.children) && item.children.length > 0) {
          item.children.forEach((child) => {
            const isDecimal = child.uomData?.[0]?.decimal === "Y" || child.isDecimal;
            const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";

            itemsSummery.itemsQty += child.weight;
            itemsSummery.items += 1;
            itemsSummery[summaryKey].totalQty += child.weight;
            itemsSummery[summaryKey].totalAmount += parseFloat(parseFloat(child.nettotal)?.toFixed(precision));
          });
        } else {
          const isDecimal = item.uomData?.[0]?.decimal === "Y" || item.isDecimal;
          const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";

          itemsSummery.itemsQty += item.weight;
          itemsSummery.items += 1;
          itemsSummery[summaryKey].totalQty += item.weight;
          itemsSummery[summaryKey].totalAmount += parseFloat(parseFloat(item.nettotal)?.toFixed(precision));
        }

        return item;
      });

      let orderTimeDetails = usePOSConfigStore.getState().orderTimeDetails || { orderStartTime: "", orderEndTime: "", paymentStartTime: "" };
      orderTimeDetails = {
        ...orderTimeDetails,
        paymentStartTime: "",
        orderStartTime: orderTimeDetails.orderStartTime !== "" ? orderTimeDetails.orderStartTime : dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss"),
      };
      usePOSConfigStore.getState().setOrderTimeDetails(orderTimeDetails);

      let updatedCart = {
        ...cart,
        items: cartItems,
        isReturn: true,
        customer: selectedReturnOrder.cwrB2cCustomer,
      };
      const isInterState = getIsInterState(updatedCart);
      const groupedItemsByTaxRate = groupItemsByTaxRate(updatedCart.items || [], precision, { isInterState });
      let updatedTotalTax = 0;
      let updatedTotalPrice = 0;
      let updatedTotalItemsQty = 0;
      let updatedTotalDiscounts = 0;
      let updatedSubtotal = 0;
      for (let i = 0; i < updatedCart.items.length; i += 1) {
        updatedTotalPrice += updatedCart.items[i].nettotal;
        updatedSubtotal += updatedCart.items[i].nettotal;
        updatedTotalItemsQty += updatedCart.items[i].weight;
        updatedTotalTax += updatedCart.items[i].taxAmount;
        updatedTotalDiscounts += updatedCart.items[i].discount ? updatedCart.items[i].discount : 0;
        updatedCart.items[i].key = i;
      }
      const prcPrecision = tillData.tillAccess.csBunit.currencies[0].prcPrecision;
      let returnTcs = 0;
      const tcsMetaData = selectedReturnOrder?.metaData?.find((m) => m.key === "tcsDetails");
      if (tcsMetaData?.value) {
        try {
          const tcsDetails = JSON.parse(tcsMetaData.value);
          const originalTcsTotal = parseFloat(tcsDetails.total) || 0;
          if (originalTcsTotal > 0 && Array.isArray(tcsDetails.items) && tcsDetails.items.length > 0) {
            const originalOrderNettotal = tcsDetails.items.reduce((sum, it) => sum + (parseFloat(it.nettotal) || 0), 0);
            if (originalOrderNettotal > 0) {
              for (let i = 0; i < updatedCart.items.length; i += 1) {
                const returnedItem = updatedCart.items[i];
                const matchedTcs = tcsDetails.items.find((it) => it.productId === (returnedItem.productId || returnedItem.product?.mProductId));
                if (matchedTcs && parseFloat(matchedTcs.amount) > 0) {
                  const fullQty = parseFloat(matchedTcs.qty) || 1;
                  const tcsPerUnit = parseFloat(matchedTcs.amount) / fullQty;
                  const returnedQty = returnedItem.weight || returnedItem.qty || 1;
                  returnedItem.itemTcs = -parseFloat((tcsPerUnit * returnedQty).toFixed(prcPrecision));
                } else {
                  returnedItem.itemTcs = 0;
                }
                returnTcs += returnedItem.itemTcs;
              }
            }
          }
        } catch (e) {
          console.error("Error parsing tcsDetails for return:", e);
        }
      }
      returnTcs = parseFloat(returnTcs.toFixed(prcPrecision));

      let finalCartObj = {
        ...updatedCart,
        items: [...updatedCart.items],
        documentno: posConfig?.disableOfflineBilling === "Y" ? selectedReturnOrder?.documentno : updatedCart.documentno,
        total: parseFloat((updatedTotalPrice + returnTcs).toFixed(prcPrecision)),
        tax: updatedTotalTax,
        discount: updatedTotalDiscounts,
        totalQty: updatedTotalItemsQty,
        subTotal: updatedSubtotal - updatedTotalTax,
        tcs: returnTcs,
        groupedItemsByTaxRate,
        itemsSummery,
      };
      setCart({ ...finalCartObj });
      setLoading(false);
      setKeyValue("2");
      return finalCartObj;
    } catch (error) {
      console.error("Error adding selected return products to cart:", error);
    } finally {
      returnCartInFlightRef.current = false;
    }
  };

  return {
    // State
    selectedReturnOrder,
    setSelectedReturnOrder,
    dataSource,
    setDataSource,
    checkedKeys,
    setCheckedKeys,
    retrunSearchType,
    setReturnSearchType,
    showReturnPurchase,
    setShowReturnPurchase,
    showReturnOptions,
    setShowReturnOptions,
    isBlindReceiptPending,
    setIsBlindReceiptPending,
    isPurchaseReturn,
    setIsPurchaseReturn,
    selectedReturnType,
    setSelectedReturnType,
    displayReturnOrderSearch,
    setDisplayReturnOrderSearch,
    returnProcessRef,
    // Handlers
    handleSalesReturnFromSideMenu,
    selectPurchaseBillForReturn,
    searchReturnHistory,
    getProductData,
    handleSaveReturnCartRecord,
    addSelectedReturnProductsToCart,
  };
};

export default useSalesReturn;
